import type {
	AssetTypeCode,
	ComplexityLevel,
	DTOOf,
	HookCode,
	HookStage,
	IChecklistTemplate,
	IChecklistTemplateItem,
	ITransitionHook,
	IWorkflow,
	IWorkflowDiagnosticFinding,
	IWorkflowRepairReport,
	IWorkflowRepairResult,
	IWorkflowResolverRule,
	IWorkflowStatus,
	IWorkflowTransition,
	IWorkspaceAssignment,
	PaginatedResponse,
	TransitionDirection,
	WorkflowDiagnosticCode,
	WorkflowJsonObject,
	WorkflowPhase,
	WorkflowRepairOutcome,
	WorkspaceCode,
} from '@atlas/types'

/**
 * Wire shapes of the workflow-administration API, derived from the backend
 * interfaces so a contract change surfaces here as a type error rather than as
 * a runtime surprise. `history` never crosses the boundary.
 */
export type WorkflowDTO = DTOOf<IWorkflow, 'history'>
export type WorkflowStatusDTO = DTOOf<IWorkflowStatus>
export type WorkflowTransitionDTO = DTOOf<IWorkflowTransition>
export type WorkflowResolverRuleDTO = DTOOf<IWorkflowResolverRule>
export type TransitionHookDTO = DTOOf<ITransitionHook>
export type ChecklistTemplateItemDTO = DTOOf<IChecklistTemplateItem>
export type ChecklistTemplateDTO = DTOOf<IChecklistTemplate> & { items: ChecklistTemplateItemDTO[] }
export type WorkspaceAssignmentDTO = DTOOf<IWorkspaceAssignment>

/** Findings and repair reports are plain data — no entity, so no Date conversion. */
export type WorkflowDiagnosticFinding = IWorkflowDiagnosticFinding
export type WorkflowRepairResult = IWorkflowRepairResult
export type WorkflowRepairReport = IWorkflowRepairReport

export type WorkflowDiagnosticsPage = PaginatedResponse<WorkflowDiagnosticFinding>

// ─── Request payloads ───────────────────────────────────────────────────────

export interface WorkflowCreateBody {
	code: string
	name: string
}

export interface WorkflowUpdateBody {
	name?: string
}

export interface WorkflowActivateBody {
	setAsDefault: boolean
}

/** Deactivation and soft-delete both need a replacement when the target is the default. */
export interface WorkflowReplacementBody {
	replacementWorkflowId?: string
}

export interface WorkflowStatusCreateBody {
	workflowId: string
	code: string
	label: string
	phase: WorkflowPhase
	position: number
	workspaceDefault: WorkspaceCode
	isInitial: boolean
	isTerminal: boolean
	canvasX?: number | null
	canvasY?: number | null
}

export type WorkflowStatusUpdateBody = Partial<Omit<WorkflowStatusCreateBody, 'workflowId'>> & { isActive?: boolean }

export interface WorkflowTransitionCreateBody {
	workflowId: string
	fromStatusId: string
	toStatusId: string
	ctaLabel: string
	direction: TransitionDirection
	position?: number
}

export type WorkflowTransitionUpdateBody = Partial<Omit<WorkflowTransitionCreateBody, 'workflowId'>> & {
	isActive?: boolean
}

export interface WorkflowResolverRuleCreateBody {
	workflowId: string
	priority: number
	assetTypeCodes: AssetTypeCode[] | null
	complexityLevels: ComplexityLevel[] | null
}

/** A rule's target workflow is a matcher outcome, not an ownership relationship — it is editable. */
export type WorkflowResolverRuleUpdateBody = Partial<WorkflowResolverRuleCreateBody> & {
	isActive?: boolean
}

export interface WorkflowRepairBody {
	workOrderIds: string[]
	dryRun: boolean
}

// ─── Transition hooks ───────────────────────────────────────────────────────

export interface TransitionHookCreateBody {
	transitionId: string
	stage: HookStage
	hookCode: HookCode
	params: WorkflowJsonObject
	position?: number
}

export type TransitionHookUpdateBody = Partial<Omit<TransitionHookCreateBody, 'transitionId'>>

// ─── Checklist templates ────────────────────────────────────────────────────

export interface ChecklistTemplateCreateBody {
	code: string
	label: string
	note?: string | null
	assetTypeCodes?: AssetTypeCode[] | null
}

export type ChecklistTemplateUpdateBody = Partial<ChecklistTemplateCreateBody> & { isActive?: boolean }

export interface ChecklistTemplateItemCreateBody {
	templateId: string
	label: string
	tag?: string | null
	required?: boolean
	position?: number
	autoChecked?: boolean
}

export type ChecklistTemplateItemUpdateBody = Partial<Omit<ChecklistTemplateItemCreateBody, 'templateId'>> & {
	isActive?: boolean
}

// ─── Workspace assignments ──────────────────────────────────────────────────
// A status's default primary surface (`assetTypeCode: null`) or a per-asset-type
// override — both are `workspace_assignment` rows (design.md D5).

export interface WorkspaceAssignmentCreateBody {
	workflowId: string
	statusId: string
	assetTypeCode: AssetTypeCode | null
	workspaceCode: WorkspaceCode
	options?: WorkflowJsonObject
}

export type WorkspaceAssignmentUpdateBody = Partial<Omit<WorkspaceAssignmentCreateBody, 'workflowId' | 'statusId'>> & {
	isActive?: boolean
}

// ─── Presentation vocabulary ────────────────────────────────────────────────

/**
 * Readable text per machine-readable finding code. Rendering keys off `code`;
 * the server's `message` is shown as detail and is never parsed to decide the
 * category.
 */
export const DIAGNOSTIC_CODE_LABEL: Record<WorkflowDiagnosticCode, string> = {
	TENANT_DEFAULT_WORKFLOW_MISSING: 'No default workflow configured',
	STATUS_WITHOUT_OUTBOUND_TRANSITION: 'Status has no way out',
	WORK_ORDER_WITHOUT_WORKFLOW: 'Work Order has no workflow',
	WORK_ORDER_INVALID_WORKFLOW: 'Work Order references an unusable workflow',
	WORK_ORDER_STATUS_NOT_AVAILABLE: 'Work Order status is not in its workflow',
	RESOLVER_RULE_INVALID_WORKFLOW: 'Resolver rule targets an unusable workflow',
}

/**
 * Readable text per repair outcome, in preview wording. Apply wording differs
 * only for `REPAIRED`, which is a statement of fact once the write has happened.
 */
export const REPAIR_OUTCOME_PREVIEW_LABEL: Record<WorkflowRepairOutcome, string> = {
	REPAIRED: 'Would repair',
	NO_OP: 'No change needed',
	STATUS_NOT_AVAILABLE: 'Cannot repair — current status is unavailable in the resolved workflow',
	UNRESOLVABLE: 'Cannot repair — no safe repair could be determined',
	TERMINAL_SKIPPED: 'Skipped — Work Order is terminal',
}

export const REPAIR_OUTCOME_APPLIED_LABEL: Record<WorkflowRepairOutcome, string> = {
	...REPAIR_OUTCOME_PREVIEW_LABEL,
	REPAIRED: 'Repaired',
}
